import { BaseApiClient } from './base'
import { AuthApi, AuthConsentData } from './auth'
import { UserBooksApi } from './user-books'
import { LibraryApi } from './library'
import { AdminApi } from './admin'
import { PreferencesApi } from './preferences'
import { MembershipApi } from './membership'
import { TransactionsApi } from './transactions'
import { TranslationCostApi } from './translation-cost'
import { MembershipConfigApi } from './membership-config'
import { CreditsApi } from './credits'
import { ReferralsApi } from './referrals'
import { RewardsApi } from './rewards'
import { ReaderApi } from './reader'

// Main API client that composes all feature modules
export class ApiClient extends BaseApiClient {
  auth: AuthApi
  userBooks: UserBooksApi
  library: LibraryApi
  admin: AdminApi
  preferences: PreferencesApi
  membership: MembershipApi
  transactions: TransactionsApi
  translationCost: TranslationCostApi
  membershipConfig: MembershipConfigApi
  credits: CreditsApi
  referrals: ReferralsApi
  rewards: RewardsApi
  reader: ReaderApi

  constructor(baseUrl: string = '/api') {
    super(baseUrl)
    this.auth = new AuthApi(baseUrl)
    this.userBooks = new UserBooksApi(baseUrl)
    this.library = new LibraryApi(baseUrl)
    this.admin = new AdminApi(baseUrl)
    this.preferences = new PreferencesApi(baseUrl)
    this.membership = new MembershipApi(baseUrl)
    this.transactions = new TransactionsApi(baseUrl)
    this.translationCost = new TranslationCostApi(baseUrl)
    this.membershipConfig = new MembershipConfigApi(baseUrl)
    this.credits = new CreditsApi(baseUrl)
    this.referrals = new ReferralsApi(baseUrl)
    this.rewards = new RewardsApi(baseUrl)
    this.reader = new ReaderApi(baseUrl)
  }

  // Health check
  async healthCheck(): Promise<{ status: string }> {
    return this.request('/health-check')
  }

  // Server configuration (mode, provider info)
  async getServerConfig(): Promise<{ server_mode: string; is_test: boolean; payment_provider: string }> {
    return this.request('/config/server')
  }

  // Legacy methods - delegate to appropriate modules for backward compatibility
  async authenticateWithGoogle(code: string, redirectUri: string, referralCode?: string, consent?: AuthConsentData) {
    return this.auth.authenticateWithGoogle(code, redirectUri, referralCode, consent)
  }

  async verifyToken() {
    return this.auth.verifyToken()
  }

  async deleteAccount(confirmation: string) {
    return this.auth.deleteAccount(confirmation)
  }

  async getUserBooks(params?: { page?: number; page_size?: number }) {
    return this.userBooks.getUserBooks(params)
  }

  async getUserBook(bookId: string) {
    return this.userBooks.getUserBook(bookId)
  }

  async deleteUserBook(bookId: string) {
    return this.userBooks.deleteUserBook(bookId)
  }

  async createUserBook(data: any) {
    return this.userBooks.createUserBook(data)
  }

  async updateUserBook(bookId: string, data: any) {
    return this.userBooks.updateUserBook(bookId, data)
  }

  async analyzeUserBookFile(file: File) {
    return this.userBooks.analyzeUserBookFile(file)
  }

  async addUserBookTranslation(data: any) {
    return this.userBooks.addUserBookTranslation(data)
  }

  async queueUserBookAutoTranslation(bookId: string, language: string, cefrLevel: string, estimateId?: string, provider: string = 'gemini') {
    return this.userBooks.queueUserBookAutoTranslation(bookId, language, cefrLevel, estimateId, provider)
  }

  async deleteUserBookTranslation(bookId: string, translationId: string) {
    return this.userBooks.deleteUserBookTranslation(bookId, translationId)
  }

  async downloadUserBook(bookId: string, translationId: string) {
    return this.userBooks.downloadUserBook(bookId, translationId)
  }

  async downloadUserBookOriginal(bookId: string) {
    return this.userBooks.downloadUserBookOriginal(bookId)
  }

  async getActiveTranslations() {
    return this.userBooks.getActiveTranslations()
  }

  async getJobStatus(jobId: number) {
    return this.userBooks.getJobStatus(jobId)
  }

  async getLibraryBooks(filters?: any) {
    return this.library.getLibraryBooks(filters)
  }

  async getLibraryBook(bookId: string) {
    return this.library.getLibraryBook(bookId)
  }

  async downloadLibraryBook(bookId: string, translationId: string) {
    return this.library.downloadLibraryBook(bookId, translationId)
  }

  async downloadLibraryBookOriginal(bookId: string) {
    return this.library.downloadLibraryBookOriginal(bookId)
  }

  async getUserPreferences() {
    return this.preferences.getUserPreferences()
  }

  async updateUserPreferences(preferences: any) {
    return this.preferences.updateUserPreferences(preferences)
  }

  async saveApiKey(provider: string, apiKey: string) {
    return this.preferences.saveApiKey(provider, apiKey)
  }

  async deleteApiKey(provider: string) {
    return this.preferences.deleteApiKey(provider)
  }

  async getAvailableProviders() {
    return this.preferences.getAvailableProviders()
  }

  async getAdminStats() {
    return this.admin.getAdminStats()
  }

  async getAdminBooks(
    includeHidden: boolean = false, 
    page: number = 1, 
    pageSize: number = 10,
    options?: {
      languages?: string[]
      genres?: string[]
      statuses?: string[]
      search?: string
      sortBy?: string
      sortOrder?: 'asc' | 'desc'
    }
  ) {
    return this.admin.getAdminBooks(includeHidden, page, pageSize, options)
  }

  async getAdminGenres() {
    return this.admin.getAdminGenres()
  }

  async getAdminBook(bookId: string) {
    return this.admin.getAdminBook(bookId)
  }

  async getAdminTranslations(
    page: number = 1, 
    pageSize: number = 10,
    options?: {
      languages?: string[]
      levels?: string[]
      formats?: string[]
      statuses?: string[]
      search?: string
      sortBy?: string
      sortOrder?: 'asc' | 'desc'
    }
  ) {
    return this.admin.getAdminTranslations(page, pageSize, options)
  }

  async getAdminUserTranslations(
    page: number = 1, 
    pageSize: number = 10,
    options?: {
      languages?: string[]
      levels?: string[]
      formats?: string[]
      statuses?: string[]
      search?: string
      sortBy?: string
      sortOrder?: 'asc' | 'desc'
    }
  ) {
    return this.admin.getAdminUserTranslations(page, pageSize, options)
  }

  async getAdminJobs(
    page: number = 1, 
    pageSize: number = 10,
    options?: {
      statuses?: string[]
      languages?: string[]
      levels?: string[]
      search?: string
      sortBy?: string
      sortOrder?: 'asc' | 'desc'
    }
  ) {
    return this.admin.getAdminJobs(page, pageSize, options)
  }

  async createOriginalBook(data: any) {
    return this.admin.createOriginalBook(data)
  }

  async updateOriginalBook(bookId: string, data: any) {
    return this.admin.updateOriginalBook(bookId, data)
  }

  async deleteOriginalBook(bookId: string) {
    return this.admin.deleteOriginalBook(bookId)
  }

  async toggleBookHidden(bookId: string, hidden: boolean) {
    return this.admin.toggleBookHidden(bookId, hidden)
  }

  async recalculateBookPages(bookId: string) {
    return this.admin.recalculateBookPages(bookId)
  }

  async bulkRecalculateBookPages(bookIds: string[]) {
    return this.admin.bulkRecalculateBookPages(bookIds)
  }

  async deleteTranslation(translationId: string) {
    return this.admin.deleteTranslation(translationId)
  }

  async updateTranslationStatus(translationId: string, status: string) {
    return this.admin.updateTranslationStatus(translationId, status)
  }

  async analyzeBookFile(file: File) {
    return this.admin.analyzeBookFile(file)
  }

  async addTranslation(data: any) {
    return this.admin.addTranslation(data)
  }

  async queueAutoTranslation(bookId: string, language: string, cefrLevel: string) {
    return this.admin.queueAutoTranslation(bookId, language, cefrLevel)
  }

  async getAdminSettings() {
    return this.admin.getAdminSettings()
  }

  async updateAdminSetting(key: string, value: string) {
    return this.admin.updateAdminSetting(key, value)
  }

  async getTranslationJobStatus(translationId: string) {
    return this.admin.getTranslationJobStatus(translationId)
  }

  async getTranslationErrorLogs(translationId: string) {
    return this.admin.getTranslationErrorLogs(translationId)
  }

  async restartTranslationJob(translationId: string) {
    return this.admin.restartTranslationJob(translationId)
  }

  async analyzeBookForSplit(bookId: string) {
    return this.admin.analyzeBookForSplit(bookId)
  }

  async splitBook(
    bookId: string, 
    sections: any[], 
    coverReplacements: Record<string, string> = {},
    options?: {
      generateCover?: boolean
      generateDescription?: boolean
      promptTemplateCover?: string
      promptTemplateDescription?: string
    }
  ) {
    return this.admin.splitBook(bookId, sections, coverReplacements, options)
  }

  async getAllJobs() {
    return this.admin.getAllJobs()
  }

  async bulkDeleteBooks(bookIds: string[]) {
    return this.admin.bulkDeleteBooks(bookIds)
  }

  async bulkQueueTranslations(data: { book_ids: string[]; translations: Array<{ language: string; cefr_level: string }>; skip_duplicates?: boolean }) {
    return this.admin.bulkQueueTranslations(data)
  }

  async generateBookCover(bookId: string, promptTemplate?: string, options?: { model?: string; useDescription?: boolean }) {
    return this.admin.generateBookCover(bookId, promptTemplate, options)
  }

  async bulkGenerateCovers(bookIds: string[], promptTemplate?: string, options?: { model?: string; useDescription?: boolean }) {
    return this.admin.bulkGenerateCovers(bookIds, promptTemplate, options)
  }

  async generateBookDescription(title: string, author: string, promptTemplate: string) {
    return this.admin.generateBookDescription(title, author, promptTemplate)
  }

  async bulkGenerateDescriptions(bookIds: string[], promptTemplate: string) {
    return this.admin.bulkGenerateDescriptions(bookIds, promptTemplate)
  }

  async generateDescriptionCover(
    bookId: string, 
    descriptionPromptTemplate: string, 
    coverPromptTemplate: string, 
    options?: { coverModel?: string }
  ) {
    return this.admin.generateDescriptionCover(bookId, descriptionPromptTemplate, coverPromptTemplate, options)
  }

  async bulkGenerateDescriptionsCovers(
    bookIds: string[], 
    descriptionPromptTemplate: string, 
    coverPromptTemplate: string, 
    options?: { coverModel?: string }
  ) {
    return this.admin.bulkGenerateDescriptionsCovers(bookIds, descriptionPromptTemplate, coverPromptTemplate, options)
  }

  async getAdminTransactions(page: number = 1, pageSize: number = 10) {
    return this.admin.getAdminTransactions(page, pageSize)
  }
}

// Global API client instance
export const apiClient = new ApiClient()

